import {
  Button,
  ButtonList,
  DropdownButton,
  Page,
  SearchInput,
  SimplePagination,
  Table,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { NotificationPayloadT, Tag } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import * as React from 'react';

import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import TagsCreateModal from 'fable/tagsCreateModal/containers/tagsCreateModal';

import { WithTagsInjectedProps } from '../../../../hocs/tags/withTags';

import { TagsTableRow } from './tagsTableRow';

const FETCH_DELAY = 1000;

export type TagsOwnProps = {};

export type TagsDispatchProps = {
  notificationAdd: (payload: NotificationPayloadT) => void;
};

export type TagsStateProps = {};

type TagsProps = WithTagsInjectedProps &
  TagsOwnProps &
  TagsDispatchProps &
  TagsStateProps;

type TagsState = {
  isCreateModalOpen: boolean;
  currentTag?: Tag;
  waitingForFetch: boolean;
};

export class Tags extends React.PureComponent<TagsProps, TagsState> {
  constructor(props: TagsProps) {
    super(props);

    this.state = {
      isCreateModalOpen: false,
      waitingForFetch: false,
    };
  }

  componentDidMount() {
    this.props.tags.getItems();
  }

  setWaitingForFetch(waitingForFetch: boolean) {
    this.setState({
      waitingForFetch,
    });
  }

  delayedFetchItems() {
    const { tags } = this.props;
    const { getItems } = tags;
    setTimeout(() => {
      getItems().then(() => {
        this.setWaitingForFetch(false);
      });
    }, FETCH_DELAY);
  }

  @boundMethod
  handleSearchSubmit(searchTerm: string) {
    this.props.tags.searchTermChange(searchTerm, 0);
  }

  @boundMethod
  handleSearchToggle(isOpen: boolean) {
    if (!isOpen && this.props.tags.searchTerm) {
      this.props.tags.searchTermChange('', 0);
    }
  }

  @boundMethod
  handleSortByNameAsc() {
    this.props.tags.sortByChange('name', 'asc');
  }

  @boundMethod
  handleSortByNameDesc() {
    this.props.tags.sortByChange('name', 'desc');
  }

  @boundMethod
  handleSortByUsageAsc() {
    this.props.tags.sortByChange('usage_count', 'asc');
  }

  @boundMethod
  handleSortByUsageDesc() {
    this.props.tags.sortByChange('usage_count', 'desc');
  }

  @boundMethod
  handleFilterAll() {
    this.props.tags.filterByChange(null);
  }

  @boundMethod
  handleFilterActive() {
    this.props.tags.filterByChange('active');
  }

  @boundMethod
  handleFilterDeactivated() {
    this.props.tags.filterByChange('inactive');
  }

  @boundMethod
  gotoNextPage() {
    this.props.tags.nextPage();
  }

  @boundMethod
  gotoPreviousPage() {
    this.props.tags.previousPage();
  }

  @boundMethod
  showCreateModal() {
    ampli.settingsTagsAddTagButtonClicked();
    this.setState({ isCreateModalOpen: true });
  }

  @boundMethod
  handleCreateModalClose(canceled = false) {
    this.setState({ isCreateModalOpen: false });

    if (!canceled) {
      this.setWaitingForFetch(true);
      this.delayedFetchItems();
    }
  }

  @boundMethod
  handleRetry() {
    this.props.tags.getItems();
  }

  renderLoadingStatus() {
    const { tags } = this.props;

    if (tags.getItemsAsyncStatus === 'failed') {
      return (
        <Table.Tr>
          <Table.Td>
            Something went wrong...
            <Button variant="tertiary" onClick={this.handleRetry}>
              Retry
            </Button>
          </Table.Td>
        </Table.Tr>
      );
    }

    return <TableLoadingRow colSpan={3} />;
  }

  renderSuccessStatus() {
    const { tags } = this.props;
    const { items, toggleActivation } = tags;

    let success: React.ReactNode;

    if (items.length > 0) {
      success = items.map((tag, i) => (
        <TagsTableRow key={i} tag={tag} toggleActivation={toggleActivation} />
      ));
    } else {
      success = (
        <Table.Tr>
          <Table.Td>No tags found.</Table.Td>
        </Table.Tr>
      );
    }

    return success;
  }

  renderFoot() {
    const { tags } = this.props;
    const { hasNextPage, hasPreviousPage, getItemsAsyncStatus } = tags;

    const foot = (
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td colSpan={5}>
            <SimplePagination
              previousButtonClick={this.gotoPreviousPage}
              previousButtonDisabled={!hasPreviousPage}
              nextButtonClick={this.gotoNextPage}
              nextButtonDisabled={!hasNextPage}
              loading={getItemsAsyncStatus === 'loading'}
            />
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    );

    return foot;
  }

  render() {
    const { tags } = this.props;
    const { hasNextPage, hasPreviousPage, getItemsAsyncStatus, filterBy } =
      tags;
    const { waitingForFetch } = this.state;

    const showFoot = hasNextPage || hasPreviousPage;

    return (
      <>
        <Page.Section
          title="All tags"
          controls={
            <>
              <SearchInput
                onSubmit={this.handleSearchSubmit}
                onToggle={this.handleSearchToggle}
              />

              <DropdownButton
                dropdown={
                  <ButtonList>
                    <ButtonList.Item
                      onClick={this.handleFilterAll}
                      isSelected={filterBy === null}
                    >
                      All tags
                    </ButtonList.Item>
                    <ButtonList.Item
                      onClick={this.handleFilterActive}
                      isSelected={filterBy === 'active'}
                    >
                      Active tags
                    </ButtonList.Item>
                    <ButtonList.Item
                      onClick={this.handleFilterDeactivated}
                      isSelected={filterBy === 'inactive'}
                    >
                      Deactivated tags
                    </ButtonList.Item>
                  </ButtonList>
                }
              >
                Filter
              </DropdownButton>

              <DropdownButton
                buttonProps={{ disabled: !!tags.searchTerm }}
                dropdown={
                  <ButtonList>
                    <ButtonList.Item
                      icon="arrow-up"
                      onClick={this.handleSortByNameAsc}
                    >
                      A - Z
                    </ButtonList.Item>
                    <ButtonList.Item
                      icon="arrow-down"
                      onClick={this.handleSortByNameDesc}
                    >
                      Z - A
                    </ButtonList.Item>
                    <ButtonList.Divider />
                    <ButtonList.Item
                      icon="arrow-up"
                      onClick={this.handleSortByUsageDesc}
                    >
                      Most used
                    </ButtonList.Item>
                    <ButtonList.Item
                      icon="arrow-down"
                      onClick={this.handleSortByUsageAsc}
                    >
                      Least used
                    </ButtonList.Item>
                  </ButtonList>
                }
              >
                Sort by
              </DropdownButton>

              <Button onClick={this.showCreateModal} id="add-tag-btn">
                Add tag
              </Button>
            </>
          }
        >
          <Table framed stickyHeader stickyFooter headerWithBackground>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tag</Table.Th>
                <Table.Th width="20%">Used</Table.Th>
                <Table.Th>Active</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {!waitingForFetch && getItemsAsyncStatus === 'succeeded'
                ? this.renderSuccessStatus()
                : this.renderLoadingStatus()}
            </Table.Tbody>

            {showFoot && this.renderFoot()}
          </Table>
        </Page.Section>

        <TagsCreateModal
          isOpen={this.state.isCreateModalOpen}
          onClose={this.handleCreateModalClose}
        />
      </>
    );
  }
}
